import { motion, AnimatePresence } from "framer-motion";
import { Pencil, X } from "lucide-react";
import { useTheme } from "@/App";
import { getCurrencySymbol } from "@/lib/utils";
import { useState, useEffect } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type YearStat = {
  earned: string;
  billedDays: string;
  dayRate: string;
  total: string;
};

export type BarEntry = {
  year: string;
  value: number;
};

// ─── Fallback seed data (shown when no yearly_performance rows exist) ─────────

const FALLBACK_STATS: Record<string, YearStat> = {
  "2023": { total: "—", earned: "—", billedDays: "—", dayRate: "—" },
};

const FALLBACK_BARS: BarEntry[] = [
  { year: "2023", value: 50 },
];

// ─── Component ────────────────────────────────────────────────────────────────

type Props = {
  totalAverage: string;
  yearlyData?: Record<string, YearStat>;
  barHeights?: BarEntry[];
};

export function RevenueChart({ totalAverage, yearlyData, barHeights }: Props) {
  const activeBars = barHeights && barHeights.length > 0 ? barHeights : FALLBACK_BARS;
  const MAX_BAR_VALUE = Math.max(...activeBars.map(b => b.value), 1);

  const CHART_H = 160;
  const BAR_W = 20;
  const BAR_GAP = 20;
  const CHART_W = activeBars.length * (BAR_W + BAR_GAP) - BAR_GAP;

  const [barStats, setBarStats] = useState<Record<string, YearStat>>(
    yearlyData && Object.keys(yearlyData).length > 0 ? yearlyData : FALLBACK_STATS
  );
  const [selectedYear, setSelectedYear] = useState<string>(() => {
    if (barHeights && barHeights.length > 0) return barHeights[barHeights.length - 1].year;
    return "2023";
  });
  const [hoveredYear, setHoveredYear] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<YearStat | null>(null);
  const [focusedField, setFocusedField] = useState<"earned" | "dayRate" | null>(null);

  // Sync when real data arrives after async load
  useEffect(() => {
    if (yearlyData && Object.keys(yearlyData).length > 0) {
      setBarStats(yearlyData);
      // Ensure selectedYear exists in the new data
      const years = Object.keys(yearlyData).sort((a, b) => parseInt(b) - parseInt(a));
      setSelectedYear(prev => (yearlyData[prev] ? prev : years[0]));
    }
  }, [yearlyData]);

  useEffect(() => {
    if (barHeights && barHeights.length > 0) {
      setSelectedYear(prev => {
        const validYears = barHeights.map(b => b.year);
        return validYears.includes(prev) ? prev : barHeights[barHeights.length - 1].year;
      });
    }
  }, [barHeights]);

  const selected = barStats[selectedYear] ?? { earned: "—", billedDays: "—", dayRate: "—", total: "—" };
  const { isDark, currency } = useTheme();
  const sym = getCurrencySymbol(currency);

  const cardBg = isDark
    ? "bg-gradient-to-br from-white/5 to-white/[0.02]"
    : "bg-white shadow-sm border-black/5";
  const textClasses = isDark ? "text-white" : "text-slate-900";
  const labelClasses = isDark ? "text-white/60" : "text-slate-600";
  const mutedClasses = isDark ? "text-white/40" : "text-slate-400";
  const borderClasses = isDark ? "border-white/10" : "border-black/5";
  const inputCls = `w-full px-4 py-3 rounded-2xl border transition-all outline-none ${isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-slate-900 placeholder-slate-300 focus:border-gray-400"}`;

  const formatCurrencyValue = (value: string) => {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return "";
    const num = Number(cleaned);
    if (Number.isNaN(num)) return value;
    return `${sym}${Math.round(num).toLocaleString('en-US')}`;
  };

  function openEdit() {
    setEditDraft({ ...barStats[selectedYear] });
    setIsEditing(true);
  }

  function saveEdit() {
    if (!editDraft) return;
    setBarStats(prev => ({
      ...prev,
      [selectedYear]: {
        ...editDraft,
        earned: formatCurrencyValue(editDraft.earned),
        dayRate: formatCurrencyValue(editDraft.dayRate),
      },
    }));
    setIsEditing(false);
    setEditDraft(null);
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditDraft(null);
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="w-full h-full"
    >
      <div className={`rounded-[2.5rem] ${cardBg} border ${borderClasses} p-12 h-full min-h-[440px] relative overflow-hidden transition-all duration-300`}>

        <div className="flex justify-between items-start mb-6 relative z-10">
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${labelClasses} uppercase tracking-[0.2em] mb-3`}>Historical Performance</h3>

            <AnimatePresence mode="wait">
              {isEditing && editDraft ? (
                <motion.div
                  key="edit"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-4 pr-4"
                >
                  <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 ${mutedClasses}`}>
                    Editing {selectedYear}
                  </div>
                  <div>
                    <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-1`}>Total Annual Earn</div>
                    <input
                      className={inputCls}
                      value={editDraft.earned}
                      onFocus={() => setFocusedField("earned")}
                      onBlur={e => {
                        setFocusedField(null);
                        setEditDraft(d => d ? { ...d, earned: formatCurrencyValue(e.target.value) } : d);
                      }}
                      onChange={e => {
                        const raw = e.target.value;
                        setEditDraft(d => d ? { ...d, earned: focusedField === "earned" ? raw : formatCurrencyValue(raw) } : d);
                      }}
                      placeholder={`${sym}0`}
                    />
                  </div>
                  <div>
                    <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-1`}>Billed Days</div>
                    <input
                      className={inputCls}
                      value={editDraft.billedDays}
                      onChange={e => setEditDraft(d => d ? { ...d, billedDays: e.target.value } : d)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-1`}>Average Day Rate</div>
                    <input
                      className={inputCls}
                      value={editDraft.dayRate}
                      onFocus={() => setFocusedField("dayRate")}
                      onBlur={e => {
                        setFocusedField(null);
                        setEditDraft(d => d ? { ...d, dayRate: formatCurrencyValue(e.target.value) } : d);
                      }}
                      onChange={e => {
                        const raw = e.target.value;
                        setEditDraft(d => d ? { ...d, dayRate: focusedField === "dayRate" ? raw : formatCurrencyValue(raw) } : d);
                      }}
                      placeholder={`${sym}0`}
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={saveEdit}
                      className="flex-1 bg-black text-white py-3 rounded-full font-bold uppercase tracking-[0.2em] hover:scale-105 active:scale-95 transition-transform text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex-1 border border-red-200 text-red-400 py-3 rounded-full font-bold uppercase tracking-[0.2em] hover:scale-105 active:scale-95 transition-all text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="view"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Total Annual Earn is the H1 hero number */}
                  <div className={`text-[10px] font-bold ${mutedClasses} uppercase tracking-wider mb-2`}>Total Annual Earn</div>
                  <div className={`text-5xl font-black ${textClasses} tracking-tight mb-8 transition-all duration-300`}>
                    {selected.earned}
                  </div>
                  <div className={`space-y-6 ${isDark ? "text-white/90" : "text-slate-700"}`}>
                    <div>
                      <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-2`}>Monthly Average</div>
                      <div className={`text-3xl font-black ${textClasses} transition-all duration-300`}>{selected.total}</div>
                    </div>
                    <div>
                      <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-2`}>Billed Days</div>
                      <div className={`text-3xl font-black ${textClasses} transition-all duration-300`}>{selected.billedDays}</div>
                    </div>
                    <div>
                      <div className={`text-xs font-bold ${mutedClasses} uppercase tracking-wider mb-2`}>Average Day Rate</div>
                      <div className={`text-3xl font-black ${textClasses} transition-all duration-300`}>{selected.dayRate}</div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={isEditing ? cancelEdit : openEdit}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer backdrop-blur-sm border ${
              isEditing
                ? isDark ? "bg-white/10 text-white border-white/10" : "bg-slate-100 text-slate-600 border-black/5"
                : isDark ? "bg-white/10 text-white/30 hover:bg-white hover:text-black border-white/10" : "bg-black/5 text-[#1A1F36]/25 hover:bg-black hover:text-white border-black/5"
            }`}
          >
            {isEditing ? <X size={13} /> : <Pencil size={13} />}
          </button>
        </div>

        {/* SVG Bar Chart */}
        {!isEditing && (
          <div className="absolute right-0 bottom-0 top-[60px] w-1/2 sm:w-2/3 p-6 flex items-end z-20">
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${CHART_W + 8} ${CHART_H + 24}`}
              preserveAspectRatio="xMidYMax meet"
              style={{ overflow: "visible" }}
            >
              {activeBars.map((entry, i) => {
                const barH = (entry.value / MAX_BAR_VALUE) * CHART_H;
                const x = i * (BAR_W + BAR_GAP);
                const y = CHART_H - barH;
                const isSelected = entry.year === selectedYear;
                const isHovered = entry.year === hoveredYear;

                const fill = isSelected
                  ? "#FCD34D"
                  : isDark
                  ? "rgba(96,165,250,0.4)"
                  : "rgba(74,108,247,0.2)";

                const scaleY = isHovered && !isSelected ? 1.06 : 1;
                const barYAdjusted = isHovered && !isSelected ? y - barH * 0.06 : y;

                return (
                  <g
                    key={entry.year}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedYear(entry.year)}
                    onMouseEnter={() => setHoveredYear(entry.year)}
                    onMouseLeave={() => setHoveredYear(null)}
                  >
                    <rect x={x - 4} y={0} width={BAR_W + 8} height={CHART_H + 20} fill="transparent" />
                    <rect
                      x={x}
                      y={barYAdjusted}
                      width={BAR_W}
                      height={barH * scaleY}
                      rx={10}
                      ry={10}
                      fill={fill}
                      style={{ transition: "y 0.15s, height 0.15s, fill 0.2s" }}
                    />
                    <text
                      x={x + BAR_W / 2}
                      y={CHART_H + 18}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight={isSelected ? 800 : isHovered ? 700 : 600}
                      letterSpacing="0.12em"
                      fill={
                        isSelected
                          ? "#FCD34D"
                          : isHovered
                          ? isDark ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)"
                          : isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.4)"
                      }
                      style={{ transition: "fill 0.15s", cursor: "pointer" }}
                    >
                      {entry.year}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </motion.div>
  );
}
